import { useEffect, useId, useState } from 'react';
import { Button, Checkbox, Modal } from '@humanforce/mango-ui-react';
import type { ApproverSplitSegment } from './payRunApproverModel';
import { NotImplementedHint } from './PayRunNotImplemented';
import './PayRunApproveDialog.css';

interface PayRunApproveDialogProps {
  visible: boolean;
  payRunTitle: string;
  /** The route role this viewer is approving as, e.g. "Finance delegate". */
  approvingAs: string | null;
  /** Net pay and Super, as supplied by the API. Empty when the run has no completed calculation. */
  split: ApproverSplitSegment[];
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const money = (value: number): string =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(value);

/**
 * PRR-402 approve confirmation. The design gates an approval behind an explicit attestation, which
 * matters here because the decision authorises real payments against a frozen calculation.
 *
 * Two deliberate departures from the Figma frame, both recorded in the implementation plan:
 *  - The hero "To approve" total is omitted. No API field carries it, and summing Net and Super in
 *    the browser would be deriving a payroll figure client-side.
 *  - The attestation starts UNCHECKED. The design shows it pre-ticked, but a pre-ticked compliance
 *    attestation is not an attestation; the approver has to make the statement themselves.
 */
export const PayRunApproveDialog = ({
  visible,
  payRunTitle,
  approvingAs,
  split,
  busy = false,
  onConfirm,
  onCancel,
}: PayRunApproveDialogProps) => {
  const attestationId = useId();
  const [attested, setAttested] = useState(false);

  // Never carry an attestation across openings — each approval is its own statement.
  useEffect(() => {
    if (!visible) setAttested(false);
  }, [visible]);

  const total = split.reduce((sum, segment) => sum + segment.amount, 0);

  return (
    <Modal
      visible={visible}
      header={`Approve ${payRunTitle}`}
      testId="pay-run-approve-dialog"
      onHide={onCancel}
      footer={(
        <>
          <Button label="Cancel" variant="tertiary" onClick={onCancel} disabled={busy} />
          <Button
            label="Approve"
            disabled={!attested || busy}
            loading={busy}
            testId="pay-run-approve-dialog-confirm"
            onClick={onConfirm}
          />
        </>
      )}
    >
      <div className="mgo-stack mgo-gap-l">
        <p className="mgo-text-s mgo-color-muted" style={{ margin: 0 }}>
          {approvingAs
            ? `You’re approving as ${approvingAs.toLowerCase()} for this pay run.`
            : 'You’re approving this pay run.'}
        </p>

        <div className="mgo-stack mgo-gap-2xs">
          <span className="mgo-text-xs mgo-color-muted">To approve</span>
          <span className="mgo-cluster mgo-gap-s mgo-align-center">
            <span className="mgo-text-l mgo-weight-strong">—</span>
            <NotImplementedHint
              what="The total being approved"
              because="No API field supplies it, and summing net and super in the browser would invent a payroll figure."
            />
          </span>
        </div>

        {split.length > 0 && total > 0 && (
          <div className="mgo-stack mgo-gap-s" data-testid="pay-run-approve-dialog-split">
            {/* A presentational proportion of two API-supplied totals — no new monetary figure. */}
            <div className="pay-run-approve-split" role="presentation">
              {split.map((segment) => (
                <span
                  key={segment.id}
                  className={`pay-run-approve-split__segment pay-run-approve-split__segment--${segment.id}`}
                  style={{ flexGrow: segment.amount }}
                />
              ))}
            </div>
            <ul className="mgo-stack mgo-gap-xs" style={{ margin: 0, padding: 0, listStyle: 'none' }}>
              {split.map((segment) => (
                <li key={segment.id} className="mgo-split mgo-align-center mgo-text-s">
                  <span>{segment.label}</span>
                  <span className="mgo-cluster mgo-gap-s">
                    <span className="mgo-weight-strong">{money(segment.amount)}</span>
                    <span className="mgo-color-muted">{Math.round((segment.amount / total) * 1000) / 10}%</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <Checkbox
          id={attestationId}
          label="I’ve reviewed this pay run and approve these payments."
          checked={attested}
          onChange={setAttested}
          testId="pay-run-approve-dialog-attestation"
        />
      </div>
    </Modal>
  );
};
